import { useState, useMemo, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  Box,
  Typography,
  Button,
  Stack,
  useMediaQuery,
  Chip,
  Card,
  CardContent,
  Avatar,
  Snackbar,
  Alert,
  IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import StarIcon from "@mui/icons-material/Star";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CodeIcon from "@mui/icons-material/Code";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import axiosInstance from "../api/axiosInstance";
import { getCourseImage } from "../utils/imageUtils";

const CoursesCarousel = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const isTablet = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [dbCourses, setDbCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
    accent: "#A380FF",
    success: "#00e676",
    text: isDark ? "#ffffff" : "#1a1a1a",
    textSecondary: isDark ? "#b0b0b0" : "#666666",
    bg: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        console.log("🔗 DEBUG: Fetching courses from", axiosInstance.defaults.baseURL);
        const courseRes = await axiosInstance.get("/course");
        // console.log("✅ DEBUG: Courses fetched:", courseRes.data);
        setDbCourses(courseRes.data);

        if (user) {
          const enrollRes = await axiosInstance.get("/course/enrollments/me");
          console.log("✅ DEBUG: Enrollments fetched:", enrollRes.data);
          setEnrolledCourses(enrollRes.data.map(e => e.course_id));
        }
      } catch (err) {
        console.error("❌ DEBUG: Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const [selectedCategory, setSelectedCategory] = useState("All");

  const categories = useMemo(() => {
    const cats = new Set(dbCourses.map((c) => c.category).filter(Boolean));
    const orderedCats = Array.from(cats).sort((a, b) => {
      if (a === "Frontend" || a === "Backend") return -1;
      if (b === "Frontend" || b === "Backend") return 1;
      return a.localeCompare(b);
    });
    return ["All", ...orderedCats];
  }, [dbCourses]);

  const filteredCourses = useMemo(() => {
    if (selectedCategory === "All") return dbCourses;
    return dbCourses.filter((c) => c.category === selectedCategory);
  }, [selectedCategory, dbCourses]);

  const settings = {
    dots: true,
    infinite: filteredCourses.length > 3,
    speed: 600,
    slidesToShow: isMobile ? 1 : isTablet ? 2 : 3,
    slidesToScroll: 1,
    arrows: !isMobile,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    cssEase: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 3 } },
      { breakpoint: 960, settings: { slidesToShow: 2, arrows: true } },
      { breakpoint: 600, settings: { slidesToShow: 1, arrows: false } },
    ],
  };

  const handleEnroll = async (course) => {
    if (!user) {
      setSnackbar({ open: true, message: "Please sign in to enroll", severity: "warning" });
      navigate("/signin");
      return;
    }

    if (course.is_paid) {
      // Razorpay Payment Flow
      try {
        const orderRes = await axiosInstance.post("/payment/create-order", { course_id: course.id });
        const { razorpay_order_id, amount, currency } = orderRes.data;

        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_your_key", // Use env variable
          amount: amount,
          currency: currency,
          name: "CodingComet",
          description: `Enrollment for ${course.title}`,
          order_id: razorpay_order_id,
          handler: async function (response) {
            try {
              await axiosInstance.post("/payment/verify", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              });
              setSnackbar({ open: true, message: "Payment successful! Enrolled.", severity: "success" });
              setEnrolledCourses(prev => [...prev, course.id]);
              navigate(`/${course.slug}`);
            } catch (err) {
              setSnackbar({ open: true, message: "Verification failed.", severity: "error" });
            }
          },
          prefill: {
            name: user.username,
            email: user.email,
          },
          theme: {
            color: colors.primary,
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      } catch (err) {
        console.error("Order creation failed", err);
        setSnackbar({ open: true, message: "Could not initiate payment.", severity: "error" });
      }
    } else {
      // Free Course Enrollment
      try {
        await axiosInstance.post(`/payment/enroll-free/${course.id}`);
        setSnackbar({ open: true, message: "Enrolled successfully!", severity: "success" });
        setEnrolledCourses(prev => [...prev, course.id]);
        navigate(`/${course.slug}`);
      } catch (err) {
        setSnackbar({ open: true, message: "Enrollment failed.", severity: "error" });
      }
    }
  };

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      <Stack direction="row" justifyContent="center" spacing={2} sx={{ mb: 5, flexWrap: "wrap", gap: 1 }}>
        {categories.map((cat) => (
          <Chip
            key={cat}
            label={cat}
            onClick={() => setSelectedCategory(cat)}
            sx={{
              px: 1, py: 2.5, fontSize: "0.95rem", fontWeight: 600, cursor: "pointer", transition: "all 0.3s ease",
              background: selectedCategory === cat ? `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"),
              color: selectedCategory === cat ? "#fff" : (isDark ? "#fff" : "#1a1a1a"),
              border: `2px solid ${selectedCategory === cat ? "transparent" : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)")}`,
              "&:hover": { transform: "translateY(-2px)" },
            }}
          />
        ))}
      </Stack>

      <Box sx={{
        "& .slick-slider": { paddingBottom: 4 },
        "& .slick-dots li button:before": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.3)", fontSize: "12px" },
        "& .slick-dots li.slick-active button:before": { color: colors.primary },
        "& .slick-arrow:before": { color: colors.primary, fontSize: "30px" },
      }}>
        {loading ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">Loading courses...</Typography>
          </Box>
        ) : filteredCourses.length === 0 ? (
          <Box sx={{ py: 10, textAlign: "center", border: `1px dashed ${theme.palette.divider}`, borderRadius: 4 }}>
            <Typography variant="h6">No courses found in this category.</Typography>
            <Typography variant="body2" color="text.secondary">Check backend connection or run seed_courses.py</Typography>
          </Box>
        ) : !isMobile ? (
          <Slider {...settings}>
            {filteredCourses.map((course) => (
              <Box key={course.id} sx={{ px: 1.5 }}>
                <CourseCard
                  course={course}
                  isDark={isDark}
                  colors={colors}
                  isEnrolled={enrolledCourses.includes(course.id)}
                  onEnroll={() => handleEnroll(course)}
                />
              </Box>
            ))}
          </Slider>
        ) : (
          <Stack spacing={3}>
            {filteredCourses.map((course) => (
              <CourseCard
                key={course.id}
                course={course}
                isDark={isDark}
                colors={colors}
                isEnrolled={enrolledCourses.includes(course.id)}
                onEnroll={() => handleEnroll(course)}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};


const CourseCard = ({ course, isDark, colors, isEnrolled, onEnroll }) => {
  const categoryColor = 
    course.category === "Frontend" ? colors.secondary :
    course.category === "Backend" ? colors.primary :
    course.category === "Artificial Intelligence" ? colors.accent :
    colors.primary;

  return (
    <Card sx={{
      height: "100%", borderRadius: "24px", overflow: "hidden", position: "relative",
      background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
      transition: "all 0.4s ease",
      display: "flex", flexDirection: "column",
      "&:hover": { transform: "translateY(-12px)", boxShadow: `0 20px 60px ${categoryColor}40`, borderColor: `${categoryColor}60` },
    }}>
      <Box sx={{ position: "relative", height: 220, overflow: "hidden", background: isDark ? "#1a1a1a" : "#f5f5f5" }}>
        <Box component="img" src={getCourseImage(course)} alt={course.title} sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease", "&:hover": { transform: "scale(1.1)" } }} />
        <Chip label={course.category} size="small" sx={{ position: "absolute", top: 16, left: 16, background: `${categoryColor}dd`, color: "#fff", fontWeight: 700, fontSize: "0.75rem", backdropFilter: "blur(10px)" }} />
        {course.is_paid && <Chip label="Premium" size="small" sx={{ position: "absolute", top: 16, right: 16, background: colors.accent, color: "#fff", fontWeight: 700, fontSize: "0.75rem" }} />}
      </Box>

      <CardContent sx={{ p: 3.5, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Typography variant="h5" fontWeight={800} sx={{ mb: 1.5, background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          {course.title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: colors.textSecondary, lineHeight: 1.7, flexGrow: 1 }}>
          {course.description}
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 2.5, flexWrap: "wrap", gap: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <MenuBookIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
            <Typography variant="caption" color={colors.textSecondary}>{course.contents?.length || 0} Lessons</Typography>
          </Stack>
          <Button
            component={Link}
            to={`/curriculum/${course.slug}`}
            sx={{ fontSize: "0.75rem", fontWeight: 700, p: 0, minWidth: 0, color: colors.secondary, textTransform: "none", "&:hover": { textDecoration: "underline" } }}
          >
            View Full Curriculum
          </Button>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: "auto", pt: 2, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}` }}>
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: colors.primary }}>
              {course.price === 0 ? "Free" : `₹${course.price}`}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={isEnrolled ? () => { } : onEnroll}
            component={isEnrolled ? Link : "button"}
            to={isEnrolled ? `/${course.slug}` : undefined}
            endIcon={isEnrolled ? <PlayCircleOutlineIcon /> : <ArrowForwardIcon />}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "12px", px: 3,
              background: isEnrolled ? "linear-gradient(135deg, #00e676, #00c853)" : `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              "&:hover": { transform: "translateX(4px)" },
            }}
          >
            {isEnrolled ? "Start Learning" : (course.is_paid ? `Enroll @ ₹${course.price}` : "Enroll Free")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CoursesCarousel;
